import React, { useState, useRef, useEffect, useCallback } from "react";
import ASSETS from "../assets.json";
import "./SpatialSection.scss";

const {
    iconPrev:              ICON_PREV,
    iconNext:              ICON_NEXT,
    videoPrecisionEditing: VIDEO_PRECISION,
    gifConfigurator:       GIF_CONFIG,
    videoCgi:              VIDEO_CGI,
    videoAr:               VIDEO_AR,
} = ASSETS.spatial;

const DEFAULT_DURATION = 4; // fallback (seconds) used before metadata loads

// duration: explicit seconds — only set for assets whose length can't be
// auto-detected (GIFs). Videos leave duration undefined; it is read from
// the video element after metadata loads.
// Parsed from GIF binary: 285 frames × ~6.67 cs/frame = 1900 cs = 19.00 s
const GIF_CONFIG_DURATION = 19;

const FEATURES = [
    {
        title: "3D Precision Editing",
        body:  "Turn products and props from AI images into editable 3D models, so you can adjust camera angles, placement, and scene details with precision. Get accurate, consistent visuals without relying on guesswork.",
        media: { type: "video", src: VIDEO_PRECISION },
    },
    {
        title: "Product Configurator",
        body:  "Let customers explore colors, materials, and variations in real time with interactive product configurators. Create a more engaging shopping experience while reducing the need for separate images.",
        media:    { type: "gif", src: GIF_CONFIG },
        duration: GIF_CONFIG_DURATION,
    },
    {
        title: "3D CGI Product Images",
        body:  "Generate high-quality CGI product images along with full asset sets, including 360 spins and all product variants. Create everything you need for ecommerce and marketing from a single 3D model.",
        media: { type: "video", src: VIDEO_CGI },
    },
    {
        title: "AR View in Room",
        body:  "Allow customers to place products in their space using AR to see size, scale, and fit in real life. Help buyers feel confident before purchasing.",
        media: { type: "video", src: VIDEO_AR },
    },
];

// ─── Media renderer ───────────────────────────────────────────────────────────
function FeatureMedia({ media, alt }) {
    if (media.type === "video") {
        return (
            <video
                className="lv2-sp-media"
                src={media.src}
                autoPlay
                muted
                loop
                playsInline
            />
        );
    }
    // "image" or "gif" — both render with <img>
    return <img className="lv2-sp-media" src={media.src} alt={alt} />;
}

// ─── Component ────────────────────────────────────────────────────────────────
const SpatialSection = () => {
    const [activeIdx, setActiveIdx] = useState(0);

    // Keyed by feature index. GIF durations are pre-populated from FEATURES data;
    // video durations are filled in after metadata is preloaded at mount.
    const [durations, setDurations] = useState(() => {
        const d = {};
        FEATURES.forEach((feat, i) => {
            if (feat.duration != null) d[i] = feat.duration;
        });
        return d;
    });

    const containerRef = useRef(null);
    const itemRefs     = useRef([]);
    const timerRef     = useRef(null);

    // Preload metadata for every video feature so durations are known before
    // the feature becomes active.
    useEffect(() => {
        FEATURES.forEach((feat, i) => {
            if (feat.media.type === "video") {
                const v = document.createElement("video");
                v.preload = "metadata";
                v.src = feat.media.src;
                v.onloadedmetadata = () => {
                    setDurations(prev => ({ ...prev, [i]: v.duration }));
                    v.src = ""; // release reference
                };
            }
        });
    }, []);

    // Duration for the currently active feature (falls back to DEFAULT while
    // video metadata is still loading — typically < 100 ms for local assets).
    const activeDuration = durations[activeIdx] ?? DEFAULT_DURATION;

    // Start (or restart) the auto-advance timeout for the given duration.
    // Uses setTimeout so each feature gets its own independent countdown.
    const startTimer = useCallback((duration) => {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            setActiveIdx(i => (i + 1) % FEATURES.length);
        }, duration * 1000);
    }, []);

    // Restart the timer whenever the active feature or its known duration changes.
    useEffect(() => {
        const dur = durations[activeIdx] ?? DEFAULT_DURATION;
        startTimer(dur);
        return () => clearTimeout(timerRef.current);
    }, [activeIdx, durations, startTimer]);

    // Scroll active feature to the top of the list container.
    useEffect(() => {
        const t = setTimeout(() => {
            const container = containerRef.current;
            const item      = itemRefs.current[activeIdx];
            if (container && item) {
                container.scrollTo({ top: item.offsetTop, behavior: "smooth" });
            }
        }, 50);
        return () => clearTimeout(t);
    }, [activeIdx]);

    const goTo = (i) => {
        if (i < 0 || i >= FEATURES.length) return;
        setActiveIdx(i);
        // Timer resets automatically via the effect above
    };

    const canPrev = activeIdx > 0;
    const canNext = activeIdx < FEATURES.length - 1;

    return (
        <div className="lv2-sp-wrapper">

            {/* ── Heading ────────────────────────────────────────────────────── */}
            <div className="lv2-sp-heading">
                <h2 className="manrope f-50 w-600 lh-60 lv2-sp-title">
                    Advanced 3D &amp; Spatial Capabilities
                </h2>
                <p className="manrope f-20 w-500 lh-28 lv2-sp-subtitle">
                    Powering our AI workflows and available as standalone services.
                </p>
            </div>

            {/* ── Two-column content ─────────────────────────────────────────── */}
            <div className="lv2-sp-content">

                {/* ── Left column ─────────────────────────────────────────────── */}
                <div className="lv2-sp-left">

                    {/* Scrollable feature list — desktop only */}
                    <div className="lv2-sp-features" ref={containerRef}>
                        {FEATURES.map((feat, i) => {
                            const isActive = i === activeIdx;
                            return (
                                <div
                                    key={i}
                                    ref={el => { itemRefs.current[i] = el; }}
                                    className={`lv2-sp-feat${isActive ? " lv2-sp-feat-active" : ""}`}
                                    onClick={() => goTo(i)}
                                >
                                    <p className="lv2-sp-feat-title">{feat.title}</p>
                                    {/* Progress bar — keyed so animation restarts when
                                        feature changes or its duration is first detected */}
                                    {isActive && (
                                        <div
                                            key={`pb-${activeIdx}-${activeDuration}`}
                                            className="lv2-sp-progress-bar"
                                            style={{ animationDuration: `${activeDuration}s` }}
                                        />
                                    )}
                                    <p className="manrope f-16 w-500 lh-24 lv2-sp-feat-body">
                                        {feat.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    {/* Bottom gradient fade — desktop only */}
                    <div className="lv2-sp-feat-fade" />

                    {/* Nav arrows — desktop only */}
                    <div className="lv2-sp-nav">
                        <button
                            className="lv2-sp-nav-btn"
                            onClick={() => goTo(activeIdx - 1)}
                            disabled={!canPrev}
                            aria-label="Previous feature"
                        >
                            <img src={ICON_PREV} alt="" className="lv2-sp-nav-icon lv2-sp-icon-up" />
                        </button>
                        <button
                            className="lv2-sp-nav-btn"
                            onClick={() => goTo(activeIdx + 1)}
                            disabled={!canNext}
                            aria-label="Next feature"
                        >
                            <img src={ICON_NEXT} alt="" className="lv2-sp-nav-icon lv2-sp-icon-down" />
                        </button>
                    </div>

                    {/* ── Mobile: active feature text — hidden on desktop ──────── */}
                    <div className="lv2-sp-mobile-feat">
                        <h3 className="lv2-sp-mobile-feat-title">{FEATURES[activeIdx].title}</h3>
                        <p className="manrope f-16 w-500 lh-24 lv2-sp-mobile-feat-body">
                            {FEATURES[activeIdx].body}
                        </p>
                    </div>

                    {/* ── Mobile: dot indicators — hidden on desktop ───────────── */}
                    <div className="lv2-sp-dots">
                        {FEATURES.map((_, i) => (
                            <button
                                key={i}
                                className={`lv2-sp-dot${i === activeIdx ? " lv2-sp-dot-active" : ""}`}
                                onClick={() => goTo(i)}
                                aria-label={`Go to feature ${i + 1}`}
                            />
                        ))}
                    </div>

                </div>

                {/* ── Right: feature media — key triggers crossfade on change ─── */}
                <div className="lv2-sp-image-wrap">
                    <FeatureMedia key={activeIdx} media={FEATURES[activeIdx].media} alt={FEATURES[activeIdx].title} />
                </div>

            </div>
        </div>
    );
};

export default SpatialSection;
